import { RealActivities, SupplierOrderDetail } from 'openapi';
import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Alert } from 'ui/Alert';
import { Severity } from 'utilities/severity';
import { useAccount } from 'context/AccountContext';
import { realActivityToDate } from 'utilities/Planning';
import { UseFormWatch } from 'react-hook-form';
import type { ExtendedMareCycleCheck } from '..';

interface ReturnType {
  activityInfoMessage: JSX.Element;
  startDate: StartDateType;
}

interface Props {
  watch: UseFormWatch<ExtendedMareCycleCheck>;
  relatedInseminationActivity: RealActivities | undefined;
  parentOrder: SupplierOrderDetail | undefined;
}

type StartDateOrigin = 'InseminationActivity' | 'Order' | undefined;

export type StartDateType = {
  date: Date;
  origin: StartDateOrigin;
};

/**
 * Small hook that will determine the start date for the mare cycle check
 */
function useMareCycleStartDateActivities({ watch, relatedInseminationActivity, parentOrder }: Props): ReturnType {
  const { formatDate: formatDatePretty } = useAccount();
  const { t } = useTranslation();

  const formInputDateValue = watch('date');

  /**
   * Determine the start date for a followup, insemination or scan date - the activities
   *
   * First we check if we have a insemination activity related to the mare cycle check.
   * Second we check if we have a parent order, if found we use the date from the order.
   * If we don't have a data at this point, we use the date from the form.
   */
  const startDate = useMemo((): StartDateType => {
    let date = new Date();
    let origin: StartDateOrigin = undefined;
    if (relatedInseminationActivity) {
      origin = 'InseminationActivity';
      date = realActivityToDate(relatedInseminationActivity);
    } else if (parentOrder) {
      origin = 'Order';
      date = new Date(parentOrder.created_on);
    } else if (formInputDateValue) {
      date = new Date(formInputDateValue + 'T00:00:00');
    }

    return {
      date,
      origin,
    };
  }, [formInputDateValue, parentOrder, relatedInseminationActivity]);

  /**
   * If we have a start date, we will show a message to the user
   * that we are using this date as the start date for the activities
   */
  const activityInfoMessage = useMemo(() => {
    if (startDate.origin === undefined) return <></>;

    return (
      <Alert
        className='text-sm'
        severity={Severity.Neurtral}
        message={t(
          'start-date-notice',
          'As we have found a related {{origin}}, we use that date ({{date}}) as the start date for the activities.',
          {
            date: formatDatePretty(startDate.date),
            origin:
              startDate.origin === 'InseminationActivity'
                ? t('insemination', 'Insemination').toLowerCase()
                : t('order', 'Order').toLowerCase(),
          },
        )}
      />
    );
  }, [formatDatePretty, startDate.date, startDate.origin, t]);

  return {
    activityInfoMessage,
    startDate,
  };
}

export default useMareCycleStartDateActivities;
